import React, { useState } from 'react'
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Table,
  Badge,
  Alert,
  Nav,
  Pagination,
} from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import useOrganization from 'hooks/use-organization'
import {
  useGetOrganizationBlockedNumbers,
  useCreateOrganizationBlockedNumber,
  useUpdateOrganizationBlockedNumber,
  useDeleteOrganizationBlockedNumber,
  useGetOrganizationBlockedAttempts,
  useGetOrganizationBlockedAttemptsStats,
} from 'hooks/use-organization-blocked-numbers'

interface BlockedNumber {
  id: string
  phone_number: string
  block_type: 'calls' | 'texts' | 'both'
  reason?: string
  created_at: string
  created_by_email?: string
}

const OrganizationBlockedNumbersPage = () => {
  const { id: orgId } = useParams<{ id: string }>()
  const [activeTab, setActiveTab] = useState<'numbers' | 'attempts'>('numbers')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedNumber, setSelectedNumber] = useState<BlockedNumber | null>(
    null
  )
  const [phoneNumber, setPhoneNumber] = useState('')
  const [blockType, setBlockType] = useState<'calls' | 'texts' | 'both'>('both')
  const [reason, setReason] = useState('')
  const [attemptFilters, setAttemptFilters] = useState({
    attempt_type: '',
    page: 1,
    per_page: 25,
  })

  const { data: org } = useOrganization(orgId)
  const { data: blockedNumbersData, isLoading: isLoadingNumbers } =
    useGetOrganizationBlockedNumbers(orgId)
  const { data: blockedAttemptsData, isLoading: isLoadingAttempts } =
    useGetOrganizationBlockedAttempts(orgId, attemptFilters)
  const { data: stats } = useGetOrganizationBlockedAttemptsStats(orgId)

  const createMutation = useCreateOrganizationBlockedNumber(orgId)
  const updateMutation = useUpdateOrganizationBlockedNumber(
    orgId,
    selectedNumber?.id || ''
  )
  const deleteMutation = useDeleteOrganizationBlockedNumber(
    orgId,
    selectedNumber?.id || ''
  )

  const handleAddNumber = async () => {
    try {
      await createMutation.mutateAsync({
        phone_number: phoneNumber,
        block_type: blockType,
        reason: reason || undefined,
      })
      toast.success('Blocked number added successfully')
      setShowAddModal(false)
      resetForm()
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error || 'Failed to add blocked number'
      )
    }
  }

  const handleUpdateNumber = async () => {
    if (!selectedNumber) return

    try {
      await updateMutation.mutateAsync({
        block_type: blockType,
        reason: reason || undefined,
      })
      toast.success('Blocked number updated successfully')
      setShowEditModal(false)
      resetForm()
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error || 'Failed to update blocked number'
      )
    }
  }

  const handleDeleteNumber = async (number: BlockedNumber) => {
    if (
      !window.confirm(
        `Are you sure you want to unblock ${number.phone_number}?`
      )
    ) {
      return
    }

    setSelectedNumber(number)
    try {
      await deleteMutation.mutateAsync()
      toast.success('Number unblocked successfully')
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to unblock number')
    }
  }

  const resetForm = () => {
    setPhoneNumber('')
    setBlockType('both')
    setReason('')
    setSelectedNumber(null)
  }

  const openEditModal = (number: BlockedNumber) => {
    setSelectedNumber(number)
    setPhoneNumber(number.phone_number)
    setBlockType(number.block_type)
    setReason(number.reason || '')
    setShowEditModal(true)
  }

  const getBlockTypeBadge = (type: string) => {
    switch (type) {
      case 'calls':
        return <Badge variant="warning">Calls Only</Badge>
      case 'texts':
        return <Badge variant="info">Texts Only</Badge>
      case 'both':
        return <Badge variant="danger">All Communication</Badge>
      default:
        return <Badge variant="secondary">{type}</Badge>
    }
  }

  const getAttemptTypeBadge = (type: string) => {
    return type === 'call' ? (
      <Badge variant="warning">Call</Badge>
    ) : (
      <Badge variant="info">Text</Badge>
    )
  }

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item href="/organizations">Organizations</Breadcrumb.Item>
        <Breadcrumb.Item href={`/organizations/${orgId}`}>
          {org?.name || 'Organization'}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Blocked Numbers</Breadcrumb.Item>
      </Breadcrumb>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Blocked Numbers - {org?.name}</h2>
      </div>

      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h5>{stats?.total_blocked || 0}</h5>
              <small className="text-muted">Total Blocked</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h5>{stats?.blocked_calls || 0}</h5>
              <small className="text-muted">Blocked Calls</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h5>{stats?.blocked_texts || 0}</h5>
              <small className="text-muted">Blocked Texts</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h5>{stats?.blocked_today || 0}</h5>
              <small className="text-muted">Blocked Today</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header>
          <Nav variant="tabs" activeKey={activeTab}>
            <Nav.Item>
              <Nav.Link
                eventKey="numbers"
                onClick={() => setActiveTab('numbers')}
              >
                Blocked Numbers
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="attempts"
                onClick={() => setActiveTab('attempts')}
              >
                Blocked Attempts
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Card.Header>

        <Card.Body>
          {activeTab === 'numbers' && (
            <>
              <div className="d-flex justify-content-between mb-3">
                <h5>Manage Blocked Numbers</h5>
                <Button onClick={() => setShowAddModal(true)}>
                  Add Blocked Number
                </Button>
              </div>

              {isLoadingNumbers ? (
                <div>Loading...</div>
              ) : blockedNumbersData?.blocked_numbers?.length === 0 ? (
                <Alert variant="info">
                  No organization-wide blocked numbers yet. Individual users may
                  still have their own blocked numbers.
                </Alert>
              ) : (
                <Table striped hover>
                  <thead>
                    <tr>
                      <th>Phone Number</th>
                      <th>Block Type</th>
                      <th>Reason</th>
                      <th>Created By</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blockedNumbersData?.blocked_numbers?.map((number) => (
                      <tr key={number.id}>
                        <td>{number.phone_number}</td>
                        <td>{getBlockTypeBadge(number.block_type)}</td>
                        <td>{number.reason || '-'}</td>
                        <td>{number.created_by_email || 'System'}</td>
                        <td>
                          {format(
                            new Date(number.created_at),
                            'MM/dd/yyyy HH:mm'
                          )}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-2"
                            onClick={() => openEditModal(number)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDeleteNumber(number)}
                          >
                            Unblock
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          )}

          {activeTab === 'attempts' && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Blocked Communication Attempts</h5>
                <Form.Control
                  as="select"
                  style={{ width: '200px' }}
                  value={attemptFilters.attempt_type}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setAttemptFilters({
                      ...attemptFilters,
                      attempt_type: e.target.value,
                      page: 1,
                    })
                  }
                >
                  <option value="">All Types</option>
                  <option value="call">Calls Only</option>
                  <option value="text">Texts Only</option>
                </Form.Control>
              </div>

              {isLoadingAttempts ? (
                <div>Loading...</div>
              ) : blockedAttemptsData?.blocked_attempts?.length === 0 ? (
                <Alert variant="info">No blocked attempts recorded yet.</Alert>
              ) : (
                <>
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Type</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Reason</th>
                        <th>Message Preview</th>
                      </tr>
                    </thead>
                    <tbody>
                      {blockedAttemptsData?.blocked_attempts?.map((attempt) => (
                        <tr key={attempt.id}>
                          <td>
                            {format(
                              new Date(attempt.created_at),
                              'MM/dd/yyyy HH:mm:ss'
                            )}
                          </td>
                          <td>{getAttemptTypeBadge(attempt.attempt_type)}</td>
                          <td>
                            {(attempt as any).from_number ||
                              attempt.from_phone ||
                              '-'}
                          </td>
                          <td>
                            {(attempt as any).to_number ||
                              attempt.to_phone ||
                              '-'}
                          </td>
                          <td>{attempt.block_reason || '-'}</td>
                          <td>
                            {(attempt as any).message_preview ? (
                              <span title={(attempt as any).message_preview}>
                                {(attempt as any).message_preview.substring(
                                  0,
                                  50
                                )}
                                {(attempt as any).message_preview.length > 50 &&
                                  '...'}
                              </span>
                            ) : (
                              '-'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {blockedAttemptsData?.meta &&
                    blockedAttemptsData.meta.total_pages > 1 && (
                      <Pagination className="justify-content-center">
                        <Pagination.First
                          onClick={() =>
                            setAttemptFilters({ ...attemptFilters, page: 1 })
                          }
                          disabled={attemptFilters.page === 1}
                        />
                        <Pagination.Prev
                          onClick={() =>
                            setAttemptFilters({
                              ...attemptFilters,
                              page: Math.max(1, attemptFilters.page - 1),
                            })
                          }
                          disabled={attemptFilters.page === 1}
                        />
                        <Pagination.Item active>
                          {attemptFilters.page} of{' '}
                          {blockedAttemptsData.meta.total_pages}
                        </Pagination.Item>
                        <Pagination.Next
                          onClick={() =>
                            setAttemptFilters({
                              ...attemptFilters,
                              page: Math.min(
                                blockedAttemptsData.meta.total_pages,
                                attemptFilters.page + 1
                              ),
                            })
                          }
                          disabled={
                            attemptFilters.page ===
                            blockedAttemptsData.meta.total_pages
                          }
                        />
                        <Pagination.Last
                          onClick={() =>
                            setAttemptFilters({
                              ...attemptFilters,
                              page: blockedAttemptsData.meta.total_pages,
                            })
                          }
                          disabled={
                            attemptFilters.page ===
                            blockedAttemptsData.meta.total_pages
                          }
                        />
                      </Pagination>
                    )}
                </>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Add Blocked Number Modal */}
      <Modal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false)
          resetForm()
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Blocked Number</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="tel"
                placeholder="+1234567890"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
              <Form.Text className="text-muted">
                Enter the phone number to block (will be normalized to E.164
                format)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Block Type</Form.Label>
              <Form.Control
                as="select"
                value={blockType}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setBlockType(e.target.value as any)
                }
              >
                <option value="both">Block All Communication</option>
                <option value="calls">Block Calls Only</option>
                <option value="texts">Block Texts Only</option>
              </Form.Control>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Reason (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Why is this number being blocked?"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowAddModal(false)
              resetForm()
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddNumber}
            disabled={!phoneNumber || createMutation.isLoading}
          >
            {createMutation.isLoading ? 'Adding...' : 'Add Blocked Number'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Blocked Number Modal */}
      <Modal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false)
          resetForm()
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Blocked Number</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control type="tel" value={phoneNumber} disabled />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Block Type</Form.Label>
              <Form.Control
                as="select"
                value={blockType}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setBlockType(e.target.value as any)
                }
              >
                <option value="both">Block All Communication</option>
                <option value="calls">Block Calls Only</option>
                <option value="texts">Block Texts Only</option>
              </Form.Control>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Reason (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Why is this number being blocked?"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowEditModal(false)
              resetForm()
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateNumber}
            disabled={updateMutation.isLoading}
          >
            {updateMutation.isLoading ? 'Updating...' : 'Update Blocked Number'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default OrganizationBlockedNumbersPage
